import { gql } from '@graphql/__generated__';

export const GET_LEADS_BY_ADDRESS = gql(/* GraphQL */ `
  query GetLeadsByAddress(
    $route: String!
    $streetNumber: String!
    $postcode: String
    $locality: String
  ) {
    find_cma_leads_by_address(
      args: {
        p_route: $route
        p_street_number: $streetNumber
        p_postcode: $postcode
        p_locality: $locality
      }
      order_by: { requalified_or_created_at: desc }
    ) {
      id
      anonymized_contact
      created_by
      origin_enquiry_id
      created_at
      requalified_at
      appraisal_reason
      sale_horizon
      appraisal_open_to_offer
      phone_number
      email_address
      contact {
        id
        first_name
        last_name
        phone_numbers(order_by: [{ primary: desc }, { verified: desc }]) {
          id
          number
        }
        emails(
          order_by: [
            { primary: desc }
            { verified: desc }
            { created_at: desc }
          ]
        ) {
          id
          email
        }
      }
      property {
        id
        route
        street_number
        postcode
        locality
      }
      lots(limit: 1) {
        id
      }
      lead_agents(where: { passed_at: { _is_null: true } }) {
        id
        claimed_at
        user_id
        user {
          ...UserAvatar
        }
      }
    }
  }
`);

export const GET_CMA_REPORTS_COUNT = gql(/* GraphQL */ `
  query GetCmaReportsCount($where: cma_reports_bool_exp) {
    cma_reports_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const STEP_MARKET_DATA_FRAGMENT = gql(/* GraphQL */ `
  fragment StepMarketData on cma_reports {
    id
    include_market_data
    market_comparison_places
    market_data_show_map
    market_data_show_chart
    market_data_map_zoom_level
    updated_at
  }
`);

export const STEP_COVER_PHOTO_CMA_FRAGMENT = gql(/* GraphQL */ `
  fragment StepCoverPhotoCma on cma_reports {
    id
    broker_id
    lead_id
    report_title
    updated_at
    language
    font_family
    doc_template {
      id
    }
    doc_template_theme_id
    doc_template_theme {
      id
      name
    }
    contact_id
    contact {
      id
    }
    broker {
      id
      show_in_agency_pages
      default_team {
        id
        branding_color
        show_in_agency_pages
        description
      }
    }
    cover_image_id
    cover_image {
      id
      url
    }
    property {
      id
      updated_at
      lat
      lng
      property_images(
        where: { display_on_brochure: { _eq: true } }
        order_by: { order_nr: desc }
      ) {
        ...ReportPropertyImage
      }
    }
  }
`);

export const STEP_HEDONISTIC_VALUATION_FRAGMENT = gql(/* GraphQL */ `
  fragment StepHedonisticValuation on cma_reports {
    id
    updated_at
    include_hedonistic_valuation
    property {
      id
      updated_at
      latest_appraisal {
        id
        updated_at
      }
      appraisals(order_by: { created_at: asc_nulls_last }) {
        id
      }
    }
  }
`);

export const CMA_REPORT_FILE_FRAGMENT = gql(/* GraphQL */ `
  fragment CmaReportFile on cma_report_files {
    cma_report_id
    file_id
    is_visible
    order_nr
    file {
      id
      name
      url
      mimetype
      created_at
    }
  }
`);

export const CMA_PROPERTY_FILE_FRAGMENT = gql(/* GraphQL */ `
  fragment CmaPropertyFile on property_files {
    property_id
    file_id
    order_nr
    file {
      id
      name
      url
      mimetype
      created_at
    }
  }
`);

export const STEP_DESCRIPTION_FRAGMENT = gql(/* GraphQL */ `
  fragment StepDescription on cma_reports {
    id
    updated_at
    include_description
    language
    description_en
    description_fr
    description_de
    description_it
    description_pl
    description_pt
    description_es
  }
`);

export const UPDATE_STEP_PHOTOS = gql(/* GraphQL */ `
  mutation UpdateStepPhotos($id: uuid!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }) {
      id
      updated_at
    }
  }
`);

export const UPDATE_STEP_POINTS_OF_INTEREST = gql(/* GraphQL */ `
  mutation UpdateStepPointsOfInterest(
    $id: uuid!
    $input: cma_reports_set_input!
  ) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      updated_at
    }
  }
`);

export const UPDATE_DESCRIPTION = gql(/* GraphQL */ `
  mutation UpdateDescription($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepDescription
    }
  }
`);

export const STEP_APPEND_FILES_FRAGMENT = gql(/* GraphQL */ `
  fragment StepAppendFiles on cma_reports {
    id
    cma_report_files(order_by: { order_nr: desc }) {
      cma_report_id
      file_id
      ...CmaReportFile
    }
    property {
      id
      property_files(order_by: { order_nr: desc }) {
        property_id
        file_id
        ...CmaPropertyFile
      }
    }
  }
`);

export const CMA_REPORT_EDITOR_FRAGMENT = gql(/* GraphQL */ `
  fragment CMAReportEditor on cma_reports {
    id
    updated_at
    language
    broker_id
    property {
      id
      updated_at
      street_number
      route
      postcode
      locality
      lat
      lng
    }
    doc_template_id
    doc_template {
      id
      content
      filename_template
      translations
    }
    doc_template_theme_id
    doc_template_theme {
      id
      name
      content
    }
  }
`);

export const STEP_INTRODUCTION_FRAGMENT = gql(/* GraphQL */ `
  fragment StepIntroduction on cma_reports {
    id
    updated_at
    introduction
  }
`);

export const STEP_PROPERTY_DETAILS_FRAGMENT = gql(/* GraphQL */ `
  fragment StepPropertyDetails on cma_reports {
    id
    updated_at
    property {
      id
      updated_at
      balcony_surface
      basement_surface
      building_condition
      building_standard
      building_volume
      built_surface
      charges
      commercial_surface
      commercial_yearly_rental_income
      construction_year
      cooling
      floor_of_flat
      garden_surface
      gross_floor_surface
      heating
      heating_distribution
      income_includes_parking
      land_surface
      living_surface
      minergie_code
      number_of_bathrooms
      number_of_bedrooms
      number_of_floors
      number_of_indoor_parkings
      number_of_outdoor_parkings
      number_of_rooms
      number_of_toilets
      parking_yearly_rental_income
      renovation_fund
      renovation_year
      residential_potential_rental_income
      residential_surface
      residential_yearly_rental_income
      terrace_surface
      usable_surface
      weighted_floor_surface
      plot_number
      number_of_residential_units
      number_of_commercial_units
      number_of_lifts
      side
      energy_consumption_class
      gas_emissions_class
      consumption
      emissions
      energy_reference_year
      energy_inspection_date
      estimated_lower_energy_cost
      estimated_higher_energy_cost
      energy_certificate_version
      micro_location
      macro_location
      is_residential_fully_rented
      is_commercial_fully_rented
      commercial_potential_rental_income
      is_parking_fully_rented
      parking_potential_rental_income
      orientation
      is_oriented_north
      is_oriented_south
      is_oriented_east
      is_oriented_west
      is_luxury
      is_child_friendly
      is_wheelchair_accessible
      has_view
      has_elevator
      has_fireplace
      has_storage_room
      is_new_development
      has_swimming_pool
      has_gym
      has_sauna
      has_wellness_area
      has_cellar
      has_attic
      has_photovoltaic_panels
      property_type {
        main_type
        name
      }
    }
  }
`);

export const STEP_FINAL_FRAGMENT = gql(/* GraphQL */ `
  fragment StepFinal on cma_reports {
    id
    updated_at
    positive_comments
    negative_comments
    suggested_market_value
    is_price_range
    price_range_min
    price_range_max
    show_probability
    probability_min
    probability_max
    probability_time_period
  }
`);

export const STEP_POTENTIAL_BUYERS_FRAGMENT = gql(/* GraphQL */ `
  fragment StepPotentialBuyers on cma_reports {
    id
    updated_at
    max_budget
    min_budget
    include_potential_buyers
    suggested_market_value
    property {
      id
      latest_appraisal {
        id
        min
        max
      }
    }
  }
`);

export const DELETE_CMA_REPORT = gql(/* GraphQL */ `
  mutation DeleteCmaReport($id: uuid!) {
    delete_cma_reports_by_pk(id: $id) {
      id
    }
  }
`);

export const DOC_TEMPLATES_CMA_REPORT = gql(/* GraphQL */ `
  query DocTemplatesCmaReport {
    doc_templates(where: { type: { _eq: cma_report }, active: { _eq: true } }) {
      id
      name
      active
      type
      ...DocTemplateDefaultLinks
    }
  }
`);

export const STEP_POIS_FRAGMENT = gql(/* GraphQL */ `
  fragment StepPois on properties {
    property_pois(order_by: [{ distance_meters: asc_nulls_last }]) {
      id
      distance_meters
      walking_distance_meters
      cycling_distance_meters
      walking_time_minutes
      cycling_time_minutes
      poi {
        id
        name
        formatted_address
        category
        types
        primary_type
        lat
        lng
      }
    }
  }
`);

export const STEP_AGENCY_PROFILE_FRAGMENT = gql(/* GraphQL */ `
  fragment StepAgencyProfile on cma_reports {
    id
    include_agency_description
    agency_team_members
    sort_display_mode
    cma_agent_transactions(
      order_by: [
        { zone: asc_nulls_last }
        { distance_in_meters: asc_nulls_last }
        { transaction: { signature_date: desc_nulls_last } }
        { transaction_id: asc }
      ]
    ) {
      id
      transaction_id
    }
    cma_user_reviews(order_by: { review: { created_at: desc } }) {
      id
      review_id
    }
  }
`);

export const GET_CMA_REPORT = gql(/* GraphQL */ `
  query GetCmaReport($id: uuid!) {
    cma_reports_by_pk(id: $id) {
      rental_comparables
      ...CMAReportEditor
      ...StepIntroduction
      ...StepCoverPhotoCma
      ...StepPropertyDetails
      ...StepDescription
      ...StepHedonisticValuation
      ...StepListingsCompare
      ...StepRentalListings
      ...StepTransactions
      ...StepCapRateValuation
      ...StepIntrinsicValuation
      ...StepFinal
      ...StepPotentialBuyers
      ...StepAgencyProfile
      ...StepAppendFiles
      ...StepMarketData
      ...CmaReportInclude
      property {
        id
        ...StepPois
      }
      __typename
    }
  }
`);

export const GET_CMA_REPORT_BROKER_SELECTIONS = gql(/* GraphQL */ `
  query GetCmaReportBrokerSelections($id: uuid!) {
    cma_reports_by_pk(id: $id) {
      id
      ...StepAgencyProfile
    }
  }
`);

export const GET_CMA_REPORT_TRANSACTIONS = gql(/* GraphQL */ `
  query GetCmaReportTransactions($id: uuid!) {
    cma_reports_by_pk(id: $id) {
      id
      ...StepTransactionsList
    }
  }
`);

export const GET_CMA_REPORT_POIS = gql(/* GraphQL */ `
  query GetCmaReportPois($id: uuid!) {
    cma_reports_by_pk(id: $id) {
      id
      property {
        id
        ...StepPois
      }
    }
  }
`);

export const UPDATE_CMA_REPORT_DOC_TEMPLATE = gql(/* GraphQL */ `
  mutation UpdateCmaReportDocTemplate(
    $id: uuid!
    $input: cma_reports_set_input!
  ) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      updated_at
      doc_template {
        id
        name
        type
      }
    }
  }
`);

export const UPDATE_CMA_COVER = gql(/* GraphQL */ `
  mutation UpdateCmaStepCover($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepCoverPhotoCma
    }
  }
`);

export const STEP_COVER_INSERT_PROPERTY_IMAGE = gql(/* GraphQL */ `
  mutation StepCoverInsertPropertyImage(
    $object: property_images_insert_input!
  ) {
    insert_property_images_one(object: $object) {
      id
      image {
        id
        url
      }
    }
  }
`);

export const UPDATE_TEAM_BRANDING_COLOR_CMA = gql(/* GraphQL */ `
  mutation UpdateTeamBrandingColorCma(
    $teamId: uuid!
    $brandingColor: String
    $reportId: uuid!
  ) {
    update_teams_by_pk(
      pk_columns: { id: $teamId }
      _set: { branding_color: $brandingColor }
    ) {
      id
      branding_color
    }
    update_cma_reports_by_pk(pk_columns: { id: $reportId }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const UPDATE_CMA_INTRODUCTION = gql(/* GraphQL */ `
  mutation UpdateCmaIntroduction($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepIntroduction
    }
  }
`);

export const UPDATE_PROPERTY_DETAILS = gql(/* GraphQL */ `
  mutation UpdatePropertyDetails($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepPropertyDetails
    }
  }
`);

export const UPDATE_HEDONISTIC_VALUATION = gql(/* GraphQL */ `
  mutation UpdateHedonisticValuation(
    $id: uuid!
    $input: cma_reports_set_input!
  ) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepHedonisticValuation
    }
  }
`);

export const UPDATE_FINAL = gql(/* GraphQL */ `
  mutation UpdateFinal($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepFinal
    }
  }
`);

export const UPDATE_POTENTIAL_BUYERS = gql(/* GraphQL */ `
  mutation UpdatePotentialBuyers($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepPotentialBuyers
    }
  }
`);

export const UPDATE_CMA_MARKET_DATA = gql(/* GraphQL */ `
  mutation UpdateCmaMarketData($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepMarketData
    }
  }
`);

export const STEP_ADD_CMA_REPORT_FILE = gql(/* GraphQL */ `
  mutation StepAddCmaReportFile(
    $object: cma_report_files_insert_input!
    $cma_report_id: uuid!
  ) {
    insert_cma_report_files_one(object: $object) {
      cma_report_id
      file_id
      cma_report {
        id
        cma_report_files(order_by: { order_nr: desc }) {
          cma_report_id
          file_id
          ...CmaReportFile
        }
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }) {
      id
      updated_at
    }
  }
`);

export const STEP_DELETE_CMA_REPORT_FILE = gql(/* GraphQL */ `
  mutation StepDeleteCmaReportFile($file_id: uuid!, $cma_report_id: uuid!) {
    delete_cma_report_files(
      where: {
        file_id: { _eq: $file_id }
        cma_report_id: { _eq: $cma_report_id }
      }
    ) {
      returning {
        cma_report_id
        file_id
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }) {
      id
      updated_at
    }
  }
`);

export const STEP_UPDATE_CMA_REPORT_FILE = gql(/* GraphQL */ `
  mutation StepUpdateCmaReportFile(
    $input: cma_report_files_set_input!
    $file_id: uuid!
    $cma_report_id: uuid!
  ) {
    update_cma_report_files(
      where: {
        file_id: { _eq: $file_id }
        cma_report_id: { _eq: $cma_report_id }
      }
      _set: $input
    ) {
      returning {
        cma_report_id
        file_id
        cma_report {
          id
          cma_report_files(order_by: { order_nr: desc }) {
            cma_report_id
            file_id
            ...CmaReportFile
          }
        }
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const STEP_UPDATE_CMA_REPORT_FILE_VISIBILITY = gql(/* GraphQL */ `
  mutation StepUpdateCmaReportFileVisibility(
    $file_id: uuid!
    $cma_report_id: uuid!
    $is_visible: Boolean!
  ) {
    update_cma_report_files(
      where: {
        file_id: { _eq: $file_id }
        cma_report_id: { _eq: $cma_report_id }
      }
      _set: { is_visible: $is_visible }
    ) {
      returning {
        cma_report_id
        file_id
        is_visible
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const STEP_APPEND_ADD_PROPERTY_FILE = gql(/* GraphQL */ `
  mutation StepAppendAddPropertyFile($object: property_files_insert_input!) {
    insert_property_files_one(object: $object) {
      property_id
      file_id
      property {
        id
        property_files(order_by: { order_nr: desc }) {
          property_id
          file_id
          ...CmaPropertyFile
        }
      }
    }
  }
`);

export const STEP_LISTINGS_COMPARE_FRAGMENT = gql(/* GraphQL */ `
  fragment StepListingsCompare on cma_reports {
    id
    include_comparables_valuation
    include_comparable_listing_details
    is_property_comparable_listings_square_meter_calculation
    show_full_idx_description
    show_comparable_listing_cards
    comparable_listings_map_zoom_level
    comparable_listings(order_by: { order_nr: asc }) {
      cma_report_id
      order_nr
      aggregates_db__merged_listings_clusters_id
    }
    property {
      id
      number_of_rooms
      number_of_bedrooms
      living_surface
      built_surface
      usable_surface
      land_surface
      locality_place {
        id
        label
      }
      state
      state_place {
        id
      }
      lat
      lng
      latest_appraisal {
        value
      }
      property_type {
        main_type
        name
      }
    }
    lead {
      id
    }
    report_source
    updated_at
  }
`);

export const STEP_LISTINGS_COMPARE_LISTINGS = gql(/* GraphQL */ `
  query StepListingsCompareListings(
    $ids: jsonb
    $ch: Boolean!
    $es: Boolean!
    $fr: Boolean!
    $it: Boolean!
    $pl: Boolean!
    $pt: Boolean!
    $lat: Float
    $lng: Float
  ) {
    ch_get_cma_listings_distance(
      args: { _lat: $lat, _lng: $lng, _listing_ids: $ids }
    ) @include(if: $ch) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        places {
          id
          label
        }
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }

    fr_get_cma_listings_distance(
      args: { _lat: $lat, _lng: $lng, _listing_ids: $ids }
    ) @include(if: $fr) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        places {
          id
          label
        }
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }

    es_get_cma_listings_distance(
      args: { _lat: $lat, _lng: $lng, _listing_ids: $ids }
    ) @include(if: $es) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        places {
          id
          label
        }
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }

    it_get_cma_listings_distance(
      args: { _lat: $lat, _lng: $lng, _listing_ids: $ids }
    ) @include(if: $it) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        places {
          id
          label
        }
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }
    pl_get_cma_listings_distance(
      args: { _lat: $lat, _lng: $lng, _listing_ids: $ids }
    ) @include(if: $pl) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        places {
          id
          label
        }
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }
    pt_get_cma_listings_distance(
      args: { _lat: $lat, _lng: $lng, _listing_ids: $ids }
    ) @include(if: $pt) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        places {
          id
          label
        }
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }
  }
`);

export const STEP_LISTINGS_COMPARE_ALGO = gql(/* GraphQL */ `
  query StepListingsCompareAlgo(
    $built_surface: Float
    $lat: Float
    $living_surface: Float
    $lng: Float
    $number_of_rooms: Int
    $property_main_type: String
    $sale_price: Float
    $usable_surface: Float
    $limit: Int
    $ch: Boolean!
    $es: Boolean!
    $fr: Boolean!
    $it: Boolean!
    $pl: Boolean!
    $pt: Boolean!
  ) {
    ch_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $ch) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }

    es_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $es) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }

    fr_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $fr) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }

    it_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $it) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }
    pl_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $pl) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }
    pt_get_similar_listings_cohorts(
      args: {
        _built_surface: $built_surface
        _lat: $lat
        _living_surface: $living_surface
        _lng: $lng
        _number_of_rooms: $number_of_rooms
        _property_main_type: $property_main_type
        _sale_price: $sale_price
        _usable_surface: $usable_surface
        _limit: $limit
      }
    ) @include(if: $pt) {
      id
      distance_in_meters
      listing {
        id
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        sale_price
        computed_price_per_sqm
        offline_since
        created_at
        country_code
        route
        street_number
        postcode
        locality
        state_id
        property_type
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
        created_at
        valid_from
      }
    }
  }
`);

export const FIND_CMA_LISTING_BY_PORTAL_ID = gql(/* GraphQL */ `
  query FindCmaListingByPortalId(
    $portal: String!
    $originalId: String!
    $isCh: Boolean!
    $isEs: Boolean!
    $isFr: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
  ) {
    ch_cma_listings(
      where: {
        portal: { _eq: $portal }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) @include(if: $isCh) {
      id
    }
    es_cma_listings(
      where: {
        portal: { _eq: $portal }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) @include(if: $isEs) {
      id
    }
    fr_cma_listings(
      where: {
        portal: { _eq: $portal }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) @include(if: $isFr) {
      id
    }
    it_cma_listings(
      where: {
        portal: { _eq: $portal }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) @include(if: $isIt) {
      id
    }
    pl_cma_listings(
      where: {
        portal: { _eq: $portal }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) @include(if: $isPl) {
      id
    }
    pt_cma_listings(
      where: {
        portal: { _eq: $portal }
        original_id: { _eq: $originalId }
        removed: { _eq: false }
      }
      limit: 1
    ) @include(if: $isPt) {
      id
    }
  }
`);

export const FIND_CMA_LISTING_BY_RAW_IDENTIFIER = gql(/* GraphQL */ `
  query FindCmaListingByRawIdentifier(
    $raw: String!
    $limit: Int!
    $isCh: Boolean!
    $isEs: Boolean!
    $isFr: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
  ) {
    ch_cma_listings(
      where: {
        _or: [
          { original_id: { _eq: $raw } }
          { agency_reference: { _eq: $raw } }
        ]
        removed: { _eq: false }
      }
      limit: $limit
    ) @include(if: $isCh) {
      id
    }
    es_cma_listings(
      where: {
        _or: [
          { original_id: { _eq: $raw } }
          { agency_reference: { _eq: $raw } }
        ]
        removed: { _eq: false }
      }
      limit: $limit
    ) @include(if: $isEs) {
      id
    }
    fr_cma_listings(
      where: {
        _or: [
          { original_id: { _eq: $raw } }
          { agency_reference: { _eq: $raw } }
        ]
        removed: { _eq: false }
      }
      limit: $limit
    ) @include(if: $isFr) {
      id
    }
    it_cma_listings(
      where: {
        _or: [
          { original_id: { _eq: $raw } }
          { agency_reference: { _eq: $raw } }
        ]
        removed: { _eq: false }
      }
      limit: $limit
    ) @include(if: $isIt) {
      id
    }
    pl_cma_listings(
      where: {
        _or: [
          { original_id: { _eq: $raw } }
          { agency_reference: { _eq: $raw } }
        ]
        removed: { _eq: false }
      }
      limit: $limit
    ) @include(if: $isPl) {
      id
    }
    pt_cma_listings(
      where: {
        _or: [
          { original_id: { _eq: $raw } }
          { agency_reference: { _eq: $raw } }
        ]
        removed: { _eq: false }
      }
      limit: $limit
    ) @include(if: $isPt) {
      id
    }
  }
`);

export const UPDATE_COMPARABLES_LIST = gql(/* GraphQL */ `
  mutation UpdateComparablesList($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      include_comparables_valuation
      updated_at
    }
  }
`);

export const INSERT_CMA_REPORTS_COMPARABLES = gql(/* GraphQL */ `
  mutation InsertCmaReportsComparables(
    $id: uuid!
    $objects: [cma_reports_comparables_insert_input!]!
  ) {
    delete_cma_reports_comparables(where: { cma_report_id: { _eq: $id } }) {
      __typename
    }

    insert_cma_reports_comparables(objects: $objects) {
      returning {
        aggregates_db__merged_listings_clusters_id
        cma_report_id
        order_nr
      }
    }
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const UPDATE_CMA_REPORTS_COMPARABLES = gql(/* GraphQL */ `
  mutation UpdateCmaReportsComparables(
    $cma_report_id: uuid!
    $listing_id: bigint!
    $order_nr: Float!
  ) {
    update_cma_reports_comparables(
      where: {
        cma_report_id: { _eq: $cma_report_id }
        aggregates_db__merged_listings_clusters_id: { _eq: $listing_id }
      }
      _set: { order_nr: $order_nr }
    ) {
      returning {
        cma_report {
          comparable_listings(order_by: { order_nr: asc }) {
            order_nr
            cma_report_id
            aggregates_db__merged_listings_clusters_id
          }
        }
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const DELETE_CMA_REPORTS_COMPARABLES = gql(/* GraphQL */ `
  mutation DeleteCmaReportsComparables(
    $cma_report_id: uuid!
    $listing_id: bigint!
  ) {
    delete_cma_reports_comparables(
      where: {
        cma_report_id: { _eq: $cma_report_id }
        aggregates_db__merged_listings_clusters_id: { _eq: $listing_id }
      }
    ) {
      returning {
        cma_report {
          comparable_listings(order_by: { order_nr: asc }) {
            order_nr
            cma_report_id
            aggregates_db__merged_listings_clusters_id
          }
        }
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

// The primary key (PK) for cma_listings consists of `id` and `state_id`.
// This is because we use partitioning by `state_id` to significantly improve performance.
// @kirillvakalov
export const COMPARABLE_CMA_LISTING_DETAILS = gql(/* GraphQL */ `
  query ComparableCmaListingDetails(
    $id: Int!
    $stateId: uuid!
    $isCh: Boolean!
    $isFr: Boolean!
    $isEs: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
  ) {
    ch_cma_listings_by_pk(id: $id, state_id: $stateId) @include(if: $isCh) {
      id
      title
      address
      offline_since
      valid_from
      description
      offline_since
      sale_price
      number_of_rooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      offer_type
      sale_price
      price_unit
      rent_net
      rent_extra
      currency
      country_code
      living_surface
      built_surface
      computed_price_per_sqm
      property_type
      floor_of_flat
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      construction_year
      property_main_type
      agency_name
      agency_contact_address
      agency_contact_person
      url
      state_id
      has_view
      has_cellar
      has_elevator
      has_fireplace
      has_storage_room
      has_swimming_pool
      is_child_friendly
      is_wheelchair_accessible
      is_new_development
      images(limit: 10, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
      created_at
    }
    fr_cma_listings_by_pk(id: $id, state_id: $stateId) @include(if: $isFr) {
      id
      title
      address
      offline_since
      valid_from
      description
      offline_since
      sale_price
      number_of_rooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      offer_type
      sale_price
      price_unit
      rent_net
      rent_extra
      currency
      country_code
      living_surface
      built_surface
      computed_price_per_sqm
      property_type
      floor_of_flat
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      construction_year
      property_main_type
      description
      offline_since
      agency_name
      agency_contact_address
      agency_contact_person
      url
      state_id
      has_view
      has_cellar
      has_elevator
      has_fireplace
      has_storage_room
      has_swimming_pool
      is_child_friendly
      is_wheelchair_accessible
      is_new_development
      images(limit: 10, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
      created_at
    }
    es_cma_listings_by_pk(id: $id, state_id: $stateId) @include(if: $isEs) {
      id
      title
      address
      offline_since
      valid_from
      description
      sale_price
      number_of_rooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      offer_type
      sale_price
      price_unit
      rent_net
      rent_extra
      currency
      country_code
      living_surface
      built_surface
      computed_price_per_sqm
      property_type
      floor_of_flat
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      construction_year
      property_main_type
      description
      agency_name
      agency_contact_address
      agency_contact_person
      url
      state_id
      has_view
      has_cellar
      has_elevator
      has_fireplace
      has_storage_room
      has_swimming_pool
      is_child_friendly
      is_wheelchair_accessible
      is_new_development
      images(limit: 10, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
      created_at
    }
    it_cma_listings_by_pk(id: $id, state_id: $stateId) @include(if: $isIt) {
      id
      title
      address
      offline_since
      valid_from
      description
      sale_price
      number_of_rooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      offer_type
      sale_price
      price_unit
      rent_net
      rent_extra
      currency
      country_code
      living_surface
      built_surface
      computed_price_per_sqm
      property_type
      floor_of_flat
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      construction_year
      property_main_type
      description
      agency_name
      agency_contact_address
      agency_contact_person
      url
      state_id
      has_view
      has_cellar
      has_elevator
      has_fireplace
      has_storage_room
      has_swimming_pool
      is_child_friendly
      is_wheelchair_accessible
      is_new_development
      images(limit: 10, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
      created_at
    }
    pl_cma_listings_by_pk(id: $id, state_id: $stateId) @include(if: $isPl) {
      id
      title
      address
      offline_since
      valid_from
      description
      sale_price
      number_of_rooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      offer_type
      sale_price
      price_unit
      rent_net
      rent_extra
      currency
      country_code
      living_surface
      built_surface
      computed_price_per_sqm
      property_type
      floor_of_flat
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      construction_year
      property_main_type
      description
      agency_name
      agency_contact_address
      agency_contact_person
      url
      state_id
      has_view
      has_cellar
      has_elevator
      has_fireplace
      has_storage_room
      has_swimming_pool
      is_child_friendly
      is_wheelchair_accessible
      is_new_development
      images(limit: 10, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
      created_at
    }
    pt_cma_listings_by_pk(id: $id, state_id: $stateId) @include(if: $isPt) {
      id
      title
      address
      offline_since
      valid_from
      description
      sale_price
      number_of_rooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      offer_type
      sale_price
      price_unit
      rent_net
      rent_extra
      currency
      country_code
      living_surface
      built_surface
      computed_price_per_sqm
      property_type
      floor_of_flat
      number_of_rooms
      number_of_bedrooms
      number_of_bathrooms
      number_of_toilets
      number_of_indoor_parkings
      number_of_outdoor_parkings
      construction_year
      property_main_type
      description
      agency_name
      agency_contact_address
      agency_contact_person
      url
      state_id
      has_view
      has_cellar
      has_elevator
      has_fireplace
      has_storage_room
      has_swimming_pool
      is_child_friendly
      is_wheelchair_accessible
      is_new_development
      images(limit: 10, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
      created_at
    }
  }
`);

export const COMPARABLE_CMA_LISTINGS = gql(/* GraphQL */ `
  query ComparableCmaListings(
    $offset: Int
    $pageSize: Int
    $lat: Float!
    $lng: Float!
    $stateId: uuid
    $postcode: String
    $locality: String
    $listingIds: jsonb
    $chWhere: ch_cma_listings_distance_bool_exp
    $frWhere: fr_cma_listings_distance_bool_exp
    $esWhere: es_cma_listings_distance_bool_exp
    $itWhere: it_cma_listings_distance_bool_exp
    $plWhere: pl_cma_listings_distance_bool_exp
    $ptWhere: pt_cma_listings_distance_bool_exp
    $isCh: Boolean!
    $isEs: Boolean!
    $isFr: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
  ) {
    ch_get_cma_listings_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _locality: $locality
        _state_id: $stateId
      }
      limit: $pageSize
      offset: $offset
      where: $chWhere
    ) @include(if: $isCh) {
      id
      distance_in_meters
      listing {
        id
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        created_at
        valid_from
        state_id
        state {
          name
        }
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    fr_get_cma_listings_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _locality: $locality
        _state_id: $stateId
      }
      limit: $pageSize
      offset: $offset
      where: $frWhere
    ) @include(if: $isFr) {
      id
      distance_in_meters
      listing {
        id
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        created_at
        valid_from
        state_id
        state {
          name
        }
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    es_get_cma_listings_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _locality: $locality
        _state_id: $stateId
      }
      limit: $pageSize
      offset: $offset
      where: $esWhere
    ) @include(if: $isEs) {
      id
      distance_in_meters
      listing {
        id
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        created_at
        valid_from
        state_id
        state {
          name
        }
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    it_get_cma_listings_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _locality: $locality
        _state_id: $stateId
      }
      limit: $pageSize
      offset: $offset
      where: $itWhere
    ) @include(if: $isIt) {
      id
      distance_in_meters
      listing {
        id
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        created_at
        valid_from
        state_id
        state {
          name
        }
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    pl_get_cma_listings_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _locality: $locality
        _state_id: $stateId
      }
      limit: $pageSize
      offset: $offset
      where: $plWhere
    ) @include(if: $isPl) {
      id
      distance_in_meters
      listing {
        id
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        created_at
        valid_from
        state_id
        state {
          name
        }
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    pt_get_cma_listings_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _locality: $locality
        _state_id: $stateId
      }
      limit: $pageSize
      offset: $offset
      where: $ptWhere
    ) @include(if: $isPt) {
      id
      distance_in_meters
      listing {
        id
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        created_at
        valid_from
        state_id
        state {
          name
        }
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
  }
`);

export const CMA_LISTINGS_BY_RADIUS = gql(/* GraphQL */ `
  query CmaListingsByRadius(
    $lat: Float!
    $lng: Float!
    $radiusMeters: Float!
    $isCh: Boolean!
    $isFr: Boolean!
    $isEs: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
    $chWhere: ch_cma_listings_distance_bool_exp
    $frWhere: fr_cma_listings_distance_bool_exp
    $esWhere: es_cma_listings_distance_bool_exp
    $itWhere: it_cma_listings_distance_bool_exp
    $plWhere: pl_cma_listings_distance_bool_exp
    $ptWhere: pt_cma_listings_distance_bool_exp
  ) {
    ch_get_cma_listings_by_radius(
      args: { _lat: $lat, _lng: $lng, _radius_meters: $radiusMeters }
      where: $chWhere
    ) @include(if: $isCh) {
      distance_in_meters
      listing {
        id
        state_id
        state {
          name
        }
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        valid_from
        created_at
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    fr_get_cma_listings_by_radius(
      args: { _lat: $lat, _lng: $lng, _radius_meters: $radiusMeters }
      where: $frWhere
    ) @include(if: $isFr) {
      distance_in_meters
      listing {
        id
        state_id
        state {
          name
        }
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        valid_from
        created_at
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    es_get_cma_listings_by_radius(
      args: { _lat: $lat, _lng: $lng, _radius_meters: $radiusMeters }
      where: $esWhere
    ) @include(if: $isEs) {
      distance_in_meters
      listing {
        id
        state_id
        state {
          name
        }
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        valid_from
        created_at
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    it_get_cma_listings_by_radius(
      args: { _lat: $lat, _lng: $lng, _radius_meters: $radiusMeters }
      where: $itWhere
    ) @include(if: $isIt) {
      listing {
        id
        state_id
        state {
          name
        }
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        valid_from
        created_at
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    pl_get_cma_listings_by_radius(
      args: { _lat: $lat, _lng: $lng, _radius_meters: $radiusMeters }
      where: $plWhere
    ) @include(if: $isPl) {
      distance_in_meters
      listing {
        id
        state_id
        state {
          name
        }
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        valid_from
        created_at
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
    pt_get_cma_listings_by_radius(
      args: { _lat: $lat, _lng: $lng, _radius_meters: $radiusMeters }
      where: $ptWhere
    ) @include(if: $isPt) {
      distance_in_meters
      listing {
        id
        state_id
        state {
          name
        }
        sale_price
        number_of_rooms
        number_of_bedrooms
        computed_surface
        land_surface
        route
        street_number
        postcode
        locality
        computed_price_per_sqm
        property_type
        offline_since
        valid_from
        created_at
        country_code
        agency_name
        visit_contact_person
        agency_contact_person
        images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
          id
          file_name
          bucket_name
        }
      }
    }
  }
`);

export const CMA_LISTINGS_BY_AREA = gql(/* GraphQL */ `
  query CmaListingsByArea(
    $isCh: Boolean!
    $isFr: Boolean!
    $isEs: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
    $chWhere: ch_cma_listings_bool_exp!
    $frWhere: fr_cma_listings_bool_exp!
    $esWhere: es_cma_listings_bool_exp!
    $itWhere: it_cma_listings_bool_exp!
    $plWhere: pl_cma_listings_bool_exp!
    $ptWhere: pt_cma_listings_bool_exp!
  ) {
    ch_cma_listings(where: $chWhere) @include(if: $isCh) {
      id
      state_id
      state {
        name
      }
      sale_price
      number_of_rooms
      number_of_bedrooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      computed_price_per_sqm
      property_type
      offline_since
      valid_from
      country_code
      agency_name
      created_at
      visit_contact_person
      agency_contact_person
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    fr_cma_listings(where: $frWhere) @include(if: $isFr) {
      id
      state_id
      state {
        name
      }
      sale_price
      number_of_rooms
      number_of_bedrooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      computed_price_per_sqm
      property_type
      offline_since
      valid_from
      country_code
      agency_name
      created_at
      visit_contact_person
      agency_contact_person
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    es_cma_listings(where: $esWhere) @include(if: $isEs) {
      id
      state_id
      state {
        name
      }
      sale_price
      number_of_rooms
      number_of_bedrooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      computed_price_per_sqm
      property_type
      offline_since
      valid_from
      country_code
      agency_name
      created_at
      visit_contact_person
      agency_contact_person
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    it_cma_listings(where: $itWhere) @include(if: $isIt) {
      id
      state_id
      state {
        name
      }
      sale_price
      number_of_rooms
      number_of_bedrooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      computed_price_per_sqm
      property_type
      offline_since
      valid_from
      country_code
      agency_name
      created_at
      visit_contact_person
      agency_contact_person
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    pl_cma_listings(where: $plWhere) @include(if: $isPl) {
      id
      state_id
      state {
        name
      }
      sale_price
      number_of_rooms
      number_of_bedrooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      computed_price_per_sqm
      property_type
      offline_since
      valid_from
      country_code
      agency_name
      created_at
      visit_contact_person
      agency_contact_person
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
    pt_cma_listings(where: $ptWhere) @include(if: $isPt) {
      id
      state_id
      state {
        name
      }
      sale_price
      number_of_rooms
      number_of_bedrooms
      computed_surface
      land_surface
      route
      street_number
      postcode
      locality
      computed_price_per_sqm
      property_type
      offline_since
      valid_from
      country_code
      agency_name
      created_at
      visit_contact_person
      agency_contact_person
      images(limit: 1, order_by: [{ image_index: asc_nulls_last }]) {
        id
        file_name
        bucket_name
      }
    }
  }
`);

export const COMPARABLE_CMA_LISTINGS_COUNT = gql(/* GraphQL */ `
  query ComparableCmaListingsCount(
    $lat: Float!
    $lng: Float!
    $stateId: uuid
    $postcode: String
    $listingIds: jsonb
    $chWhere: ch_cma_listings_distance_bool_exp
    $frWhere: fr_cma_listings_distance_bool_exp
    $esWhere: es_cma_listings_distance_bool_exp
    $itWhere: it_cma_listings_distance_bool_exp
    $plWhere: pl_cma_listings_distance_bool_exp
    $ptWhere: pt_cma_listings_distance_bool_exp
    $isCh: Boolean!
    $isEs: Boolean!
    $isFr: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
  ) {
    ch_get_cma_listings_distance_aggregate(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _state_id: $stateId
      }
      where: $chWhere
    ) @include(if: $isCh) {
      aggregate {
        count
      }
    }
    fr_get_cma_listings_distance_aggregate(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _state_id: $stateId
      }
      where: $frWhere
    ) @include(if: $isFr) {
      aggregate {
        count
      }
    }
    es_get_cma_listings_distance_aggregate(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _state_id: $stateId
      }
      where: $esWhere
    ) @include(if: $isEs) {
      aggregate {
        count
      }
    }
    it_get_cma_listings_distance_aggregate(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _state_id: $stateId
      }
      where: $itWhere
    ) @include(if: $isIt) {
      aggregate {
        count
      }
    }
    pl_get_cma_listings_distance_aggregate(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _state_id: $stateId
      }
      where: $plWhere
    ) @include(if: $isPl) {
      aggregate {
        count
      }
    }
    pt_get_cma_listings_distance_aggregate(
      args: {
        _lat: $lat
        _lng: $lng
        _listing_ids: $listingIds
        _postcode: $postcode
        _state_id: $stateId
      }
      where: $ptWhere
    ) @include(if: $isPt) {
      aggregate {
        count
      }
    }
  }
`);

export const GENERATE_CMA_REPORT = gql(/* GraphQL */ `
  mutation GenerateCmaReport(
    $leadId: uuid!
    $source: cma_report_source_enum_enum!
  ) {
    generate_cma_report(lead_id: $leadId, source: $source) {
      cma_report_id
    }
  }
`);

export const CREATE_CMA_REPORT = gql(/* GraphQL */ `
  mutation CreateCmaReport($input: cma_reports_insert_input!) {
    insert_cma_reports_one(
      object: $input
      on_conflict: { constraint: cma_reports_pkey, update_columns: [] }
    ) {
      id
    }
  }
`);

export const CMA_REPORT_INCLUDE_FRAGMENT = gql(/* GraphQL */ `
  fragment CmaReportInclude on cma_reports {
    id
    include_cover
    include_introduction
    include_property
    include_description
    include_photos
    include_pois
    include_poi_map
    poi_map_zoom_level
    include_hedonistic_valuation
    include_comparables_valuation
    include_rental_comparables
    comparable_listings_map_zoom_level
    include_transactions
    transaction_map_zoom_level
    include_cuprate_valuation
    include_intrinsic_valuation
    include_final_value
    include_potential_buyers
    include_agent
    include_agent_agency
    include_client_testimonial
    include_append_files
    include_market_data
  }
`);

export const UPDATE_CMA_REPORT_STEP_VISIBILITY = gql(/* GraphQL */ `
  mutation UpdateCmaReportStepVisibility(
    $id: uuid!
    $input: cma_reports_set_input!
  ) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      ...CmaReportInclude
      updated_at
    }
  }
`);

export const STEP_CAP_RATE_VALUATION_FRAGMENT = gql(/* GraphQL */ `
  fragment StepCapRateValuation on cma_reports {
    id
    updated_at
    caprate_estimated_monthly_rent
    caprate_minimum_required_yield
    include_rental_comparables
    rental_comparables
    property {
      id
      lat
      lng
      computed_surface
      number_of_rooms
      number_of_bedrooms
      property_type {
        name
        main_type
      }
      latest_appraisal {
        id
        value
        rent_value
      }
    }
  }
`);

export const STEP_INTRINSIC_VALUATION_FRAGMENT = gql(/* GraphQL */ `
  fragment StepIntrinsicValuation on cma_reports {
    id
    updated_at
    construction_cost_base
    intrinsic_building_surface
    intrinsic_construction_cost
    intrinsic_annual_deprecation_rate
    intrinsic_land_value
    intrinsic_custom_fields
    property {
      id
      building_volume
      construction_year
      renovation_year
      land_surface
      living_surface
      usable_surface
      basement_surface
      commercial_surface
      balcony_surface
      garden_surface
      gross_floor_surface
      residential_surface
      terrace_surface
      weighted_floor_surface
    }
  }
`);

export const UPDATE_CAP_RATE_VALUATION = gql(/* GraphQL */ `
  mutation UpdateCapRateValuation($id: uuid!, $input: cma_reports_set_input!) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepCapRateValuation
    }
  }
`);

export const UPDATE_INTRINSIC_VALUATION = gql(/* GraphQL */ `
  mutation UpdateIntrinsicValuation(
    $id: uuid!
    $input: cma_reports_set_input!
  ) {
    update_cma_reports_by_pk(pk_columns: { id: $id }, _set: $input) {
      ...StepIntrinsicValuation
    }
  }
`);

export const REORDER_CMA_REPORTS_PROPERTY_TRANSACTIONS = gql(/* GraphQL */ `
  mutation UpdateCmaReportsTransactions(
    $cma_report_id: uuid!
    $cma_property_transaction_id: uuid!
    $order_nr: Int!
  ) {
    __typename
    update_cma_reports_property_transactions_by_pk(
      pk_columns: { id: $cma_property_transaction_id }
      _set: { order_nr: $order_nr }
    ) {
      __typename
      id
      order_nr
      cma_report_id
      transaction_id
      created_at
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      __typename
      id
      updated_at
    }
  }
`);

export const UPDATE_CMA_PROPERTY_TRANSACTION_INCLUDE = gql(/* GraphQL */ `
  mutation UpdateCmaPropertyTransactionInclude(
    $cma_report_id: uuid!
    $id: uuid!
    $include_in_median: Boolean!
  ) {
    update_cma_reports_property_transactions_by_pk(
      pk_columns: { id: $id }
      _set: { include_in_median: $include_in_median }
    ) {
      id
      include_in_median
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const CLEAR_PRICE_EVOLUTION_CACHE = gql(/* GraphQL */ `
  mutation ClearPriceEvolutionCache($cma_report_id: uuid!) {
    update_cma_reports_property_transactions(
      where: { cma_report_id: { _eq: $cma_report_id } }
      _set: {
        price_evolution_rate: null
        price_evolution_rate_updated_at: null
      }
    ) {
      affected_rows
      returning {
        id
        cma_report_id
        transaction_id
        order_nr
        created_at
        price_evolution_rate
        price_evolution_rate_updated_at
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const DELETE_CMA_REPORTS_PROPERTY_TRANSACTIONS = gql(/* GraphQL */ `
  mutation DeleteCmaReportsTransactions(
    $cma_property_transaction_id: uuid!
    $cma_report_id: uuid!
  ) {
    delete_cma_reports_property_transactions_by_pk(
      id: $cma_property_transaction_id
    ) {
      id
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
      ...StepTransactionsList
    }
  }
`);

export const STEP_TRANSACTIONS_LIST_FRAGMENT = gql(/* GraphQL */ `
  fragment StepTransactionsList on cma_reports {
    id
    cma_property_transactions(order_by: { order_nr: asc }) {
      id
      cma_report_id
      order_nr
      source
      transaction_id
      external_id
      created_at
      distance_in_meters
      include_in_median
      price_evolution_rate
      price_evolution_rate_updated_at
      transaction {
        purchase_price
        signature_date
        id
        locality
        postcode
        living_surface
        built_surface
        land_surface
        number_of_rooms
        number_of_bedrooms
        property_type_name
        property_main_type
        locality_place {
          id
          name
          label
        }
      }
    }
  }
`);

export const STEP_TRANSACTIONS_FRAGMENT = gql(/* GraphQL */ `
  fragment StepTransactions on cma_reports {
    id
    include_transactions
    adjust_price_by_evolution
    is_property_transaction_square_meter_calculation
    transaction_map_zoom_level
    ...StepTransactionsList
    property {
      id
      locality_id
      country_code
      lat
      lng
      property_type {
        id
        name
        label
        main_type
      }
      latest_appraisal {
        value
      }
    }

    updated_at
  }
`);

export const REPLACE_CMA_REPORTS_PROPERTY_TRANSACTIONS = gql(/* GraphQL */ `
  mutation ReplaceCmaReportsTransactions(
    $cma_report_id: uuid!
    $transactions: [cma_reports_property_transactions_insert_input!]!
    $crm_transaction_ids: [uuid!]!
    $external_transaction_ids: [String!]!
    $to_delete_ids: [uuid!]!
  ) {
    delete_cma_reports_property_transactions(
      where: {
        cma_report_id: { _eq: $cma_report_id }
        _or: [
          { id: { _in: $to_delete_ids } }
          { transaction_id: { _in: $crm_transaction_ids } }
          {
            _and: [
              { source: { _eq: "dvf_fr" } }
              { external_id: { _in: $external_transaction_ids } }
            ]
          }
        ]
      }
    ) {
      affected_rows
    }

    insert_cma_reports_property_transactions(objects: $transactions) {
      returning {
        id
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
      ...StepTransactionsList
    }
  }
`);

export const UPDATE_CMA_REPORT_ADJUST_PRICE = gql(/* GraphQL */ `
  mutation UpdateCmaReportAdjustPrice(
    $id: uuid!
    $adjust_price_by_evolution: Boolean!
  ) {
    update_cma_reports_by_pk(
      pk_columns: { id: $id }
      _set: { adjust_price_by_evolution: $adjust_price_by_evolution }
    ) {
      id
      adjust_price_by_evolution
      updated_at
    }
  }
`);

export const UPDATE_CMA_REPORT_TRANSACTIONS_SQUARE_METER_CALCULATION =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportTransactionsSquareMeterCalculation(
      $id: uuid!
      $is_property_transaction_square_meter_calculation: Boolean!
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: {
          is_property_transaction_square_meter_calculation: $is_property_transaction_square_meter_calculation
        }
      ) {
        id
        is_property_transaction_square_meter_calculation
        updated_at
      }
    }
  `);

export const UPDATE_CMA_REPORT_TRANSACTION_MAP_ZOOM_LEVEL = gql(/* GraphQL */ `
  mutation UpdateCmaReportTransactionMapZoomLevel(
    $id: uuid!
    $transaction_map_zoom_level: Int
  ) {
    update_cma_reports_by_pk(
      pk_columns: { id: $id }
      _set: { transaction_map_zoom_level: $transaction_map_zoom_level }
    ) {
      id
      transaction_map_zoom_level
      updated_at
    }
  }
`);

export const UPDATE_CMA_REPORT_COMPARABLE_LISTINGS_MAP_ZOOM_LEVEL =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportComparableListingsMapZoomLevel(
      $id: uuid!
      $comparable_listings_map_zoom_level: Int
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: {
          comparable_listings_map_zoom_level: $comparable_listings_map_zoom_level
        }
      ) {
        id
        comparable_listings_map_zoom_level
        updated_at
      }
    }
  `);

export const UPDATE_CMA_REPORT_COMPARABLES_SQUARE_METER_CALCULATION =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportComparableListingsSquareMeterCalculation(
      $id: uuid!
      $is_property_comparable_listings_square_meter_calculation: Boolean!
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: {
          is_property_comparable_listings_square_meter_calculation: $is_property_comparable_listings_square_meter_calculation
        }
      ) {
        id
        is_property_comparable_listings_square_meter_calculation
        updated_at
      }
    }
  `);

export const EXTERNAL_TRANSACTION_DISTANCE_FIELDS_FRAGMENT = gql(/* GraphQL */ `
  fragment ExternalTransactionDistanceFields on fr_external_transactions_distance {
    id
    source
    external_id
    country_code
    currency
    signature_date
    purchase_price
    property_main_type
    property_type_name
    living_surface
    land_surface
    built_surface
    number_of_rooms
    number_of_bedrooms
    locality
    postcode
    street_number
    street_suffix
    street_type
    street_name
    lat
    lng
    distance_in_meters
  }
`);

export const EXTERNAL_TRANSACTION_FIELDS_FRAGMENT = gql(/* GraphQL */ `
  fragment ExternalTransactionFields on fr_external_transactions {
    id
    source
    external_id
    country_code
    currency
    signature_date
    purchase_price
    property_main_type
    property_type_name
    living_surface
    land_surface
    built_surface
    number_of_rooms
    number_of_bedrooms
    locality
    postcode
    street_number
    street_suffix
    street_type
    street_name
    lat
    lng
  }
`);

export const GET_AUTOMATIC_EXTERNAL_TRANSACTIONS_QUERY = gql(/* GraphQL */ `
  query GetAutomaticExternalTransactions(
    $lat: Float!
    $lng: Float!
    $source: _text!
    $property_main_type: _text
    $cutoff_date: date
    $cutoff_date_max: date
  ) {
    fr_get_external_transactions_distance(
      args: {
        _lat: $lat
        _lng: $lng
        _max_distance_meters: 50000
        _max_results: 10
        _source: $source
        _property_main_type: $property_main_type
        _cutoff_date: $cutoff_date
        _cutoff_date_max: $cutoff_date_max
      }
    ) {
      ...ExternalTransactionDistanceFields
    }
  }
`);

export const GET_AUTOMATIC_TRANSACTIONS_QUERY = gql(/* GraphQL */ `
  query GetAutomaticTransactions(
    $target_property_id: uuid!
    $max_signature_date: date
  ) {
    get_property_transactions_distance(
      args: {
        p_target_property_id: $target_property_id
        p_max_distance_meters: 50000
        p_max_results: 10
        p_match_main_type: true
        p_years_back: 2
        p_max_signature_date: $max_signature_date
      }
    ) {
      id
      transaction_id
      distance_in_meters
      transaction {
        id
        purchase_price
        signature_date
        id
        postcode
        living_surface
        land_surface
        built_surface
        number_of_rooms
        number_of_bedrooms
        property_type_name
        property_main_type
      }
    }
  }
`);

export const CMA_TRANSACTIONS_WITH_DISTANCE_ROWS_QUERY = gql(/* GraphQL */ `
  query CmaTransactionsWithDistanceRows(
    $target_property_id: uuid!
    $max_distance_meters: float8
    $where: property_transactions_distance_bool_exp
    $order_by: [property_transactions_distance_order_by!]
    $limit: Int
    $offset: Int
  ) {
    get_property_transactions_distance(
      args: {
        p_target_property_id: $target_property_id
        p_max_distance_meters: $max_distance_meters
      }
      where: $where
      order_by: $order_by
      limit: $limit
      offset: $offset
    ) {
      id
      transaction_id
      distance_in_meters
      transaction {
        id
        purchase_price
        signature_date
        locality
        living_surface
        built_surface
        land_surface
        number_of_rooms
        number_of_bedrooms
        postcode
        property_type_name
        property_main_type
        locality_place {
          id
          name
          label
        }
      }
    }
  }
`);

export const CMA_TRANSACTIONS_WITH_DISTANCE_COUNT_QUERY = gql(/* GraphQL */ `
  query CmaTransactionsWithDistanceCount(
    $target_property_id: uuid!
    $max_distance_meters: float8
    $where: property_transactions_distance_bool_exp
  ) {
    get_property_transactions_distance_aggregate(
      args: {
        p_target_property_id: $target_property_id
        p_max_distance_meters: $max_distance_meters
      }
      where: $where
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const CMA_EXTERNAL_TRANSACTIONS_WITH_DISTANCE_ROWS_QUERY =
  gql(/* GraphQL */ `
    query CmaExternalTransactionsWithDistanceRows(
      $lat: Float!
      $lng: Float!
      $source: _text!
      $max_distance_meters: Float
      $max_results: Int
      $property_main_type: _text
      $cutoff_date: date
      $cutoff_date_max: date
      $postcode: String
      $living_surface_min: Float
      $living_surface_max: Float
      $land_surface_min: Float
      $land_surface_max: Float
      $rooms_min: Int
      $rooms_max: Int
      $price_min: Int
      $price_max: Int
      $polygon_geojson: String
      $sort_field: String
      $sort_direction: String
    ) {
      fr_get_external_transactions_distance(
        args: {
          _lat: $lat
          _lng: $lng
          _source: $source
          _max_distance_meters: $max_distance_meters
          _max_results: $max_results
          _property_main_type: $property_main_type
          _cutoff_date: $cutoff_date
          _cutoff_date_max: $cutoff_date_max
          _postcode: $postcode
          _living_surface_min: $living_surface_min
          _living_surface_max: $living_surface_max
          _land_surface_min: $land_surface_min
          _land_surface_max: $land_surface_max
          _rooms_min: $rooms_min
          _rooms_max: $rooms_max
          _price_min: $price_min
          _price_max: $price_max
          _polygon_geojson: $polygon_geojson
          _sort_field: $sort_field
          _sort_direction: $sort_direction
        }
      ) {
        ...ExternalTransactionDistanceFields
      }
    }
  `);

export const CMA_EXTERNAL_TRANSACTIONS_WITH_DISTANCE_COUNT_QUERY =
  gql(/* GraphQL */ `
    query CmaExternalTransactionsWithDistanceCount(
      $lat: Float!
      $lng: Float!
      $source: _text!
      $max_distance_meters: Float
      $property_main_type: _text
      $cutoff_date: date
      $cutoff_date_max: date
      $postcode: String
      $living_surface_min: Float
      $living_surface_max: Float
      $land_surface_min: Float
      $land_surface_max: Float
      $rooms_min: Int
      $rooms_max: Int
      $price_min: Int
      $price_max: Int
      $polygon_geojson: String
    ) {
      fr_get_external_transactions_distance_aggregate(
        args: {
          _lat: $lat
          _lng: $lng
          _source: $source
          _max_distance_meters: $max_distance_meters
          _max_results: 2001
          _property_main_type: $property_main_type
          _cutoff_date: $cutoff_date
          _cutoff_date_max: $cutoff_date_max
          _postcode: $postcode
          _living_surface_min: $living_surface_min
          _living_surface_max: $living_surface_max
          _land_surface_min: $land_surface_min
          _land_surface_max: $land_surface_max
          _rooms_min: $rooms_min
          _rooms_max: $rooms_max
          _price_min: $price_min
          _price_max: $price_max
          _polygon_geojson: $polygon_geojson
        }
      ) {
        aggregate {
          count
        }
      }
    }
  `);

export const GET_PLACE_GEOMETRIES_QUERY = gql(/* GraphQL */ `
  query GetPlaceGeometries($ids: [uuid!]!, $withGeometry: Boolean!) {
    places(where: { id: { _in: $ids } }) {
      id
      children_bbox
      simplified_geom @include(if: $withGeometry)
    }
  }
`);

export const GET_SAVED_EXTERNAL_TRANSACTIONS_QUERY = gql(/* GraphQL */ `
  query GetSavedExternalTransactions(
    $source: String!
    $externalIds: [String!]!
  ) {
    fr_external_transactions(
      where: { source: { _eq: $source }, external_id: { _in: $externalIds } }
    ) {
      ...ExternalTransactionFields
    }
  }
`);

export const GET_CURRENT_USER_TEAM_OPT_IN_STATUS = gql(/* GraphQL */ `
  query GetCurrentUserTeamOptInStatus {
    get_me {
      me {
        id
        default_team {
          id
          name
          share_transactions_in_cma
        }
        teams_users(where: { deactivated: { _eq: false } }) {
          team_id
          is_leader
          team {
            id
            name
          }
        }
      }
    }
  }
`);

export const UPDATE_TEAM_SHARE_TRANSACTIONS_IN_CMA = gql(/* GraphQL */ `
  mutation UpdateTeamShareTransactionsInCma(
    $team_id: uuid!
    $share_transactions_in_cma: Boolean!
    $cma_report_id: uuid!
  ) {
    update_teams_by_pk(
      pk_columns: { id: $team_id }
      _set: { share_transactions_in_cma: $share_transactions_in_cma }
    ) {
      id
      share_transactions_in_cma
      updated_at
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }) {
      id
      updated_at
    }
  }
`);

export const DELETE_CMA_AGENT_TRANSACTION = gql(/* GraphQL */ `
  mutation DeleteCmaAgentTransaction(
    $cma_report_id: uuid!
    $transaction_id: uuid!
  ) {
    delete_cma_reports_agent_transactions(
      where: {
        _and: [
          { cma_report_id: { _eq: $cma_report_id } }
          { transaction_id: { _eq: $transaction_id } }
        ]
      }
    ) {
      returning {
        cma_report_id
        transaction_id
      }
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const INSERT_CMA_AGENT_TRANSACTION = gql(/* GraphQL */ `
  mutation InsertCmaAgentTransaction(
    $cma_report_id: uuid!
    $transaction_id: uuid!
    $distance_in_meters: numeric
    $zone: Int
  ) {
    insert_cma_reports_agent_transactions_one(
      object: {
        cma_report_id: $cma_report_id
        transaction_id: $transaction_id
        distance_in_meters: $distance_in_meters
        zone: $zone
      }
    ) {
      ...CmaAgentTransactionDetails
    }

    update_cma_reports_by_pk(pk_columns: { id: $cma_report_id }, _set: {}) {
      id
      updated_at
    }
  }
`);

export const CMA_AGENT_TRANSACTION_FRAGMENT = gql(/* GraphQL */ `
  fragment CmaAgentTransactionDetails on cma_reports_agent_transactions {
    id
    transaction_id
    distance_in_meters
    zone
    created_at
    transaction {
      id
      signature_date
      purchase_price
      property {
        id
        locality
        postcode
        number_of_rooms
        number_of_bedrooms
        living_surface
        built_surface
        country_code
        property_images(limit: 1, order_by: { order_nr: asc }) {
          image {
            id
            url
          }
        }
      }
    }
  }
`);

export const GET_CMA_AGENT_TRANSACTIONS = gql(/* GraphQL */ `
  query GetCmaAgentTransactions($cmaReportId: uuid!) {
    cma_reports_by_pk(id: $cmaReportId) {
      id
      sort_display_mode
      cma_agent_transactions(
        order_by: [
          { zone: asc_nulls_last }
          { distance_in_meters: asc_nulls_last }
          { transaction: { signature_date: desc_nulls_last } }
          { transaction_id: asc }
        ]
      ) {
        ...CmaAgentTransactionDetails
      }
    }
  }
`);

export const GET_CMA_AGENT_TRANSACTIONS_PAGINATED = gql(/* GraphQL */ `
  query GetCmaAgentTransactionsPaginated(
    $cmaReportId: uuid!
    $sortMode: String!
    $limit: Int!
    $offset: Int!
  ) {
    get_cma_agent_transactions(
      args: {
        p_cma_report_id: $cmaReportId
        p_sorting_mode: $sortMode
        p_limit: $limit
        p_offset: $offset
      }
    ) {
      ...CmaAgentTransactionDetails
    }
  }
`);

export const UPDATE_CMA_AGENT_SORTING_MODE = gql(/* GraphQL */ `
  mutation UpdateCmaAgentSortingMode(
    $cmaReportId: uuid!
    $sortMode: String!
    $objects: [cma_reports_agent_transactions_insert_input!]!
    $hasTransactions: Boolean!
  ) {
    delete_cma_reports_agent_transactions(
      where: { cma_report_id: { _eq: $cmaReportId } }
    ) {
      affected_rows
    }

    insert_cma_reports_agent_transactions(objects: $objects)
      @include(if: $hasTransactions) {
      affected_rows
    }

    update_cma_reports_by_pk(
      pk_columns: { id: $cmaReportId }
      _set: { sort_display_mode: $sortMode }
    ) {
      ...StepAgencyProfile
      updated_at
    }
  }
`);

export const UPDATE_CMA_REPORT_INCLUDE_AGENCY_DESCRIPTION = gql(/* GraphQL */ `
  mutation UpdateCmaReportIncludeAgencyDescription(
    $id: uuid!
    $include_agency_description: Boolean!
  ) {
    update_cma_reports_by_pk(
      pk_columns: { id: $id }
      _set: { include_agency_description: $include_agency_description }
    ) {
      id
      include_agency_description
      updated_at
    }
  }
`);

export const GET_CMA_AGENCY_TEAM_MEMBERS = gql(/* GraphQL */ `
  query GetCmaAgencyTeamMembers($id: uuid!) {
    cma_reports_by_pk(id: $id) {
      id
      agency_team_members
      broker {
        id
        default_team {
          id
          teams_users(
            order_by: [{ created_at: desc }, { user_id: asc }]
            where: { deactivated: { _eq: false } }
          ) {
            user_id
            title
            user {
              ...UserAvatar
              show_in_agency_pages
            }
          }
        }
      }
    }
  }
`);

export const UPDATE_CMA_REPORT_AGENCY_TEAM_MEMBERS = gql(/* GraphQL */ `
  mutation UpdateCmaReportAgencyTeamMembers(
    $id: uuid!
    $agency_team_members: jsonb!
  ) {
    update_cma_reports_by_pk(
      pk_columns: { id: $id }
      _set: { agency_team_members: $agency_team_members }
    ) {
      __typename
      id
      agency_team_members
      updated_at
    }
  }
`);

export const CMA_USER_REVIEW_DETAILS = gql(/* GraphQL */ `
  fragment CmaUserReviewDetails on cma_reports_user_reviews {
    id
    review_id
    created_at
    review {
      id
      display_name
      first_name
      last_name
      rating
      created_at
      review_text
      is_seller
      google_address {
        formatted_address
      }
      user {
        first_name
        last_name
      }
    }
  }
`);

export const GET_CMA_USER_REVIEWS = gql(/* GraphQL */ `
  query GetCmaUserReviews($cmaReportId: uuid!) {
    cma_reports_by_pk(id: $cmaReportId) {
      id
      cma_user_reviews(order_by: { review: { created_at: desc } }) {
        ...CmaUserReviewDetails
      }
    }
  }
`);

export const DELETE_CMA_USER_REVIEW = gql(/* GraphQL */ `
  mutation DeleteCmaUserReview($cmaReportId: uuid!, $reviewId: uuid!) {
    delete_cma_reports_user_reviews(
      where: {
        _and: [
          { cma_report_id: { _eq: $cmaReportId } }
          { review_id: { _eq: $reviewId } }
        ]
      }
    ) {
      affected_rows
    }

    update_cma_reports_by_pk(pk_columns: { id: $cmaReportId }, _set: {}) {
      id
      updated_at
      cma_user_reviews(order_by: { review: { created_at: desc } }) {
        id
        review_id
      }
    }
  }
`);

export const INSERT_CMA_USER_REVIEW = gql(/* GraphQL */ `
  mutation InsertCmaUserReview($cmaReportId: uuid!, $reviewId: uuid!) {
    insert_cma_reports_user_reviews_one(
      object: { cma_report_id: $cmaReportId, review_id: $reviewId }
    ) {
      id
      cma_report_id
      review_id
    }

    update_cma_reports_by_pk(pk_columns: { id: $cmaReportId }, _set: {}) {
      id
      updated_at
      cma_user_reviews(order_by: { review: { created_at: desc } }) {
        id
        review_id
      }
    }
  }
`);

export const GET_CMA_BROKER_REVIEWS_PAGINATED = gql(/* GraphQL */ `
  query GetCmaBrokerReviewsPaginated(
    $brokerId: uuid!
    $limit: Int!
    $offset: Int!
  ) {
    reviews(
      where: {
        user_id: { _eq: $brokerId }
        completed: { _eq: true }
        review_text: { _is_null: false }
        _not: { review_text: { _eq: "" } }
      }
      order_by: [{ created_at: desc }]
      limit: $limit
      offset: $offset
    ) {
      id
      display_name
      first_name
      last_name
      rating
      created_at
      review_text
      is_seller
      google_address {
        formatted_address
      }
      user {
        first_name
        last_name
      }
    }
  }
`);

export const UPDATE_CMA_REPORT_COMPARABLES_SHOW_FULL_IDX_DESCRIPTION =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportComparablesShowFullIdxDescription(
      $id: uuid!
      $show_full_idx_description: Boolean!
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: { show_full_idx_description: $show_full_idx_description }
      ) {
        id
        updated_at
        show_full_idx_description
      }
    }
  `);

export const UPDATE_CMA_REPORT_INCLUDE_COMPARABLE_LISTING_DETAILS =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportIncludeComparableListingDetails(
      $id: uuid!
      $include_comparable_listing_details: Boolean!
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: {
          include_comparable_listing_details: $include_comparable_listing_details
        }
      ) {
        id
        updated_at
        include_comparable_listing_details
      }
    }
  `);

export const UPDATE_CMA_REPORT_SHOW_COMPARABLE_LISTING_CARDS =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportShowComparableListingCards(
      $id: uuid!
      $show_comparable_listing_cards: Boolean!
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: { show_comparable_listing_cards: $show_comparable_listing_cards }
      ) {
        id
        updated_at
        show_comparable_listing_cards
      }
    }
  `);

export const UPDATE_CMA_REPORT_INCLUDE_RENTAL_COMPARABLE_LISTING_DETAILS =
  gql(/* GraphQL */ `
    mutation UpdateCmaReportIncludeRentalComparableListingDetails(
      $id: uuid!
      $include_rental_comparable_listing_details: Boolean!
    ) {
      update_cma_reports_by_pk(
        pk_columns: { id: $id }
        _set: {
          include_rental_comparable_listing_details: $include_rental_comparable_listing_details
        }
      ) {
        id
        updated_at
        include_rental_comparable_listing_details
      }
    }
  `);

export const STATE_BY_COORDINATES = gql(/* GraphQL */ `
  query StateByCoordinates(
    $point: geometry!
    $isCh: Boolean!
    $isFr: Boolean!
    $isEs: Boolean!
    $isIt: Boolean!
    $isPl: Boolean!
    $isPt: Boolean!
  ) {
    ch_places(
      where: { type: { _eq: "state" }, geom: { _st_contains: $point } }
      limit: 1
    ) @include(if: $isCh) {
      id
      name
    }
    fr_places(
      where: { type: { _eq: "state" }, geom: { _st_contains: $point } }
      limit: 1
    ) @include(if: $isFr) {
      id
      name
    }
    es_places(
      where: { type: { _eq: "state" }, geom: { _st_contains: $point } }
      limit: 1
    ) @include(if: $isEs) {
      id
      name
    }
    it_places(
      where: { type: { _eq: "state" }, geom: { _st_contains: $point } }
      limit: 1
    ) @include(if: $isIt) {
      id
      name
    }
    pl_places(
      where: { type: { _eq: "state" }, geom: { _st_contains: $point } }
      limit: 1
    ) @include(if: $isPl) {
      id
      name
    }
    pt_places(
      where: { type: { _eq: "state" }, geom: { _st_contains: $point } }
      limit: 1
    ) @include(if: $isPt) {
      id
      name
    }
  }
`);
